import { F0Select, F0SelectItemObject } from '@factorialco/f0-react'
import {
  LedgerAccountSelectorF0Document,
  LedgerAccountSelectorF0Query,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

export type LedgerAccountOption = NonNullable<
  LedgerAccountSelectorF0Query['finance']
>['accountsConnection']['edges'][number]['node']

/** Partial type that can be used to set an initial value with just the id */
export type LedgerAccountPartialValue = {
  id: number
  name?: string | null
  number?: string | null
}

export interface LedgerAccountSelectorF0Props {
  /** The selected value - can be a full LedgerAccountOption or just a partial with id */
  value?: LedgerAccountPartialValue | null
  onChange: (ledgerAccount: LedgerAccountOption | null) => void
  legalEntityId?: number
  types?: string[]
  label?: string
  placeholder?: string
  error?: string
  required?: boolean
  disabled?: boolean
  /** ids of accounts to not show on the selector */
  skipLedgerAccountIds?: number[]
}

export function formatAccountName(
  account?: { name?: string | null; number?: string | null } | null
): string | null {
  if (!account) return null
  const parts = [account.number ?? '', account.name ?? ''].filter(Boolean)
  return parts.join(' - ') || null
}

const getAccountLabel = (account: LedgerAccountOption): string => {
  return formatAccountName(account) ?? ''
}

export const LedgerAccountSelectorF0 = ({
  value,
  onChange,
  legalEntityId,
  types,
  label = i18n.t('banking.accounts.accounting.ledger_account'),
  placeholder = i18n.t('banking.accounts.accounting.accounting_account_hint'),
  error,
  required,
  disabled,
  skipLedgerAccountIds,
}: LedgerAccountSelectorF0Props) => {
  const source = useGraphqlDataSource(
    {
      document: LedgerAccountSelectorF0Document,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({
        search: search || undefined,
        legalEntityIds: legalEntityId ? [legalEntityId] : undefined,
        filter: types?.length ? { type: { in: types } } : undefined,
      }),
      mapData: (data) => {
        const accounts = data.finance?.accountsConnection?.edges?.map((edge) => edge.node) ?? []
        if (skipLedgerAccountIds?.length) {
          return accounts.filter((account) => !skipLedgerAccountIds.includes(account.id))
        }
        return accounts
      },
      mapPagination: (data) => ({
        total: data.finance?.accountsConnection?.totalCount ?? 0,
        cursor: data.finance?.accountsConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.finance?.accountsConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    [legalEntityId, types, skipLedgerAccountIds]
  )

  const mapOptions = (
    account: LedgerAccountOption
  ): F0SelectItemObject<string, LedgerAccountOption> => ({
    value: String(account.id),
    label: getAccountLabel(account),
    item: account,
  })

  return (
    <F0Select<string, LedgerAccountOption>
      label={label}
      placeholder={placeholder}
      source={source}
      mapOptions={mapOptions}
      value={value ? String(value.id) : undefined}
      onChange={(selectedValue: string, originalItem?: LedgerAccountOption) => {
        onChange(originalItem ?? null)
      }}
      error={error}
      required={required}
      disabled={disabled}
      clearable
    />
  )
}
